
import { Component, Model, Prop, Vue, Watch, Emit } from "vue-property-decorator";
import vSelect3 from "vselect3";
import { validationMixin } from "vuelidate";
import { email, maxLength, requiredIf } from "vuelidate/lib/validators";
import { filter, debounce } from "lodash";
import api from "@/services/api.service";
import { Getter, namespace } from "vuex-class";
import { UserPermissions, PaginatedSearchQueryParams } from "@/store/types";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import notificationTemplatesApi from "@/services/api.notificationTemplate.service";
import NotificationDocumentTypeId from "@/types/sv-data/enums/NotificationDocumentTypeIds";
import NotificationTemplateTypeId from "@/types/sv-data/enums/NotificationTemplateTypeIds";
import SetEventOutcomeAndNote from "@/components/site-monitor/SetEventOutcomeAndNote.vue";
import EventOutcomeAndNote from "@/types/sv-data/EventOutcomeAndNote";
import { NotificationTemplateManualField } from "@/types/sv-data/notification-templates/NotificationTemplateManualField";
import axios, { CancelTokenSource } from "axios";
import ContactGroup from "@/types/sv-data/contact-groups/ContactGroup";
import { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import { getDebouncePeriod } from '@/utils/debounce-helper';
import CustomDefinedField from "@/components/custom-fields/CustomDefinedField.vue";
import { CustomFieldDto, CustomFieldTableTypes, CustomFieldValues } from "@/store/custom-fields/types";
import NotificationTemplateFormatOption, { notificationTemplateFormats } from "@/types/sv-data/notification-templates/NotificationTemplateFormatOption";
import NotificationRule from "@/types/sv-data/notification-rule/NotificationRule";
import ContactEmailSelect from "@/components/contacts/ContactEmailSelect.vue";
import { NotifyNotificationRules, NotifyRuleReload, NotifySent, NotifySkipped } from "@/store/site-monitor/types";
import organizationApi from "@/services/api.organizationSettings";
import FlexibleLayout from "../flexible-layout/FlexibleLayout.vue";
import { EventNotification } from "@/types/sv-data/notification-rule/EventNotification";
import { NotificationMixins, Notification, ManualFieldInput, ContactOption, NotificationNoteMaxLength, NoNotificationTemplateErrorIdentifier , NotificationState } from "@/mixins/NotificationMixin";
import { EmailNotificationRequestWithData } from "@/types/sv-data/events/EmailNotificationRequest";
import { BOverlay } from "bootstrap-vue";
import NotificationSummary from "@/components/site-monitor/NotificationSummary.vue";
import UpdateEventOutcomeRequest from "@/types/sv-data/requests/UpdateEventOutcome";
import { Layout } from "@/types/Layout";
import { EventOutcome } from '@/types/EventOutcome';
import { sortOrderByNullLast } from "@/utils/CustomFieldLogic";
import eventsApi from '@/services/api.event.service';
const CustomFields = namespace("customFields");
const SiteMonitor = namespace("siteMonitor");
const MAX_NOTE_LENGTH: number = 5000;
const FULL_SIZE_COLUMN: number = 12;

interface SentOverlayDetails {
	sentBy: string;
	sentAt: string;
	notificationTitle: string;
	contactsNames: string;
	contactGroupsTitles: string;
	hasAdditionalContacts: boolean;
}

@Component({
	components: {
		"v-select-3": vSelect3,
		"set-event-outcome-and-note": SetEventOutcomeAndNote,
		"custom-field": CustomDefinedField,
		"contact-email-select": ContactEmailSelect,
		"flexible-layout": FlexibleLayout,
		"b-overlay": BOverlay,
		"notification-summary": NotificationSummary
	},
	mixins: [validationMixin],
	validations: {
		note: {
			maxLength: maxLength(MAX_NOTE_LENGTH)
		},
		addresses: {
			required: requiredIf(function () {
				return this.selectedContactGroups && this.selectedContactGroups.length < 1
			}),
			$each: {
				email: { email }
			}
		},
		selectedContactGroups: {
			required: requiredIf(function () {
				return this.addresses && this.addresses.length < 1
			}),
		}
	}
})
export default class NotificationModalContent extends Vue {
	@Prop({ type: Number }) readonly groupId: number;
	@Prop({ type: Number }) readonly eventId: number;
	@Model("change", { type: Boolean }) readonly isVisible: boolean;

	@Prop({type: Boolean, required: false, default: true})
	private notifyOpenedManually: boolean;

	@Prop({ type: Object, required: false})
	private notifyNotificationRules: NotifyNotificationRules;

	@Prop({ type: Boolean, required: false, default: false })
	private isPoppedOut: boolean;

	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter getNotificationTemplateDefaultFormat: number;

	@CustomFields.State private notificationTemplateCustomFields: CustomFieldDto[];
	@CustomFields.Action retrieveCustomFields: ({ tableType, live, skipCancel }) => Promise<void>;

	@SiteMonitor.Getter("notificationModalLayout") private notificationModalLayout: Layout;

	private layout: Layout = {
		left: [],
		center: ["contacts", "contact-groups", "notification-note", "attach-sit-rep", "notification-templates", "event-outcome", "event-note", "custom-fields"],
		right: []
	}
	private centerColSpan: number = 0;
	private rightColSpan: number = 8;
	private leftColSpan: number = 4;

	private emails: ContactOption[] = [];
	private allContacts: ContactOption[] = [];
	private emailsLoaded = false;

	private addresses: ContactOption[] = [];
	private selectedContactGroups: ContactGroup[] = [];
	private note = "";
	private predefinedNotificationTemplates: NotificationTemplate[] = [];
	private allNotificationTemplates: NotificationTemplate[] = [];
	private selectedTemplateFormat: NotificationTemplateFormatOption = null;
	private orderedCustomFieldList: CustomFieldDto[] = [];
	private eventOutcome: EventOutcomeAndNote = {
		valid: true,
		eventOutcomeNote: null,
		eventOutcomeId: null,
		eventOutcomePath: []
	};
	private defaultNotificationTemplateId: number = null;

	private contactGroupSearchToken: CancelTokenSource = null;
	private contactGroups: ContactGroup[] = [];
	private notificationTemplateFormats: NotificationTemplateFormatOption[] = notificationTemplateFormats;
	private useSingleNotificationUI: boolean = false;

	private sentTemplates: EventNotification[] = [];
	private notificationsToSend: NotificationState[] = [];
	private notificationValidationErrorsByTemplate = new Map<number, string[]>();
	private manualNotificationIsValid: boolean = false;
	private showActionPlanAlert: boolean = false;
	private footerStep: number = 1;
	private landingPageFooterStep: number = Number.MAX_SAFE_INTEGER;

	private requiredContactMessage: string = "This contact was not included when originally sent, but is required now as a part of a notification rule.";
	private recipientInputsTouched: boolean = false;
	private highlightedContactGroups: ContactGroup[] = [];
	private isComponentReady: boolean = false;
	private currentNotificationPreviouslySent: boolean = false;
	private currentNotificationSkipped: boolean = false;

	private get selectedNotificationTemplate(): NotificationTemplate {
		return this.currentNotificationToSend?.notificationTemplate;
	}

	private get hasOneTemplate(): boolean {
		return !this.isPredefineTemplateEnabled || this.notifyOpenedManually || this.notificationRulesHasNoTemplate;
	}

	private set selectedNotificationTemplate(notificationTemplate: NotificationTemplate) {
		if (!this.currentNotificationToSend) {
			return;
		}

		this.currentNotificationToSend.notificationTemplate = notificationTemplate;
	}

	private get footerStepTotal(): number {
		return this.notificationsToSend.length || 1;
	}

	private onPrevFooterStep(): void {
		if (this.footerStep != this.landingPageFooterStep && this.footerStep > 1) {
			this.validateNotification(this.currentNotificationToSend);
			this.footerStep--;
			this.setSelectedNotificationTemplateByStep();
		}
		else {
			this.footerStep = this.maxFooterStep
		}
	}

	private get isSitRepEnabledByDefault(): boolean {
		return !this.isNotificationTemplatesEnabled;
	}

	private onNextFooterStep(): void {
		// Validate current template before moving onto next
		this.validateNotification(this.currentNotificationToSend);

		if (this.isOnFinalTemplateStep) {
			this.footerStep = this.landingPageFooterStep
			this.currentNotificationPreviouslySent = false;
			this.currentNotificationSkipped = false;
		}

		if (this.footerStep < this.footerStepTotal) {
			this.footerStep++;
			this.setSelectedNotificationTemplateByStep();
		}
	}

	private setSelectedNotificationTemplateByStep(): void {
		const notification = this.notificationsToSend[this.footerStep - 1];
		if (notification && notification.notificationTemplate) {
			this.selectedNotificationTemplate = notification.notificationTemplate;
		}
	}

	private async mounted(): Promise<void> {
		await this.loadContacts();
		await this.loadNotificationTemplates();

		this.updateModalLayout();
		await this.retrieveSentTemplates();

		if (this.isContactGroupsEnabled) {
			await this.retrieveContactGroups();
		}

		if (this.isNotificationTemplatesCustomFieldsEnabled) {
			await this.retrieveCustomFields({ tableType: CustomFieldTableTypes.NotificationTemplate, live: true, skipCancel: false });
			this.orderedCustomFieldList = this.notificationTemplateCustomFields;
			sortOrderByNullLast(this.orderedCustomFieldList);
		}

		if (!this.notifyOpenedManually) {
			this.collateNotificationsToSend();
			this.selectedNotificationTemplate = this.notificationsToSend[0]?.notificationTemplate;

		} else {
			// Create empty notification to send if in the ad hoc Notify modal
			this.notificationsToSend = [
				NotificationMixins.methods.createNotification(
					[],
					[],
					[],
					[],
					[],
					[],
					null,
					this.isSitRepEnabledByDefault
				)
			];

			this.selectedNotificationTemplate = null;
		}

		// If opened manually or predefine template feature is disabled, load the default template
		if (this.notifyOpenedManually || !this.isPredefineTemplateEnabled) {
			await this.loadDefaultTemplate();
		}

		this.isComponentReady = true;
	}

	@Watch("notifyOpenedManually")
	private generateMultipleTemplates(): void {
		this.notificationsToSend = [];
		if (!this.notifyOpenedManually) {
			this.collateNotificationsToSend();
			this.selectedNotificationTemplate = this.notificationsToSend[0]?.notificationTemplate;
			this.updateNotificationValidationErrors();
		}
	}

	@Watch("notifyNotificationRules")
	private collateNotificationsToSend(): void{
		// Fetch Contacts and Contact Groups from rules which have no notification template assigned, and rules which contain the same template multiple times
		if (!this.notifyNotificationRules || !this.notifyNotificationRules.rules) {
			return;
		}

		this.useSingleNotificationUI = false;

		const groupingMethod = this.isPredefineTemplateEnabled ? 
			NotificationMixins.methods.groupContactsAndGroupsByTemplate : 
			NotificationMixins.methods.groupContactsAndGroups;

		groupingMethod.bind(NotificationMixins.methods);

		const {
			contactsByTemplate,
			contactGroupsByTemplate,
			contactsWithNoTemplate,
			contactGroupsWithNoTemplate,
			emailAddressesWithNoTemplate
		} = groupingMethod(this.notifyNotificationRules?.rules);

		if (this.isPredefineTemplateEnabled && !this.notifyOpenedManually
			&& (contactsByTemplate.size > 0 || contactGroupsByTemplate.size > 0))
		{
			this.buildMultipleNotificationsForEndEvent(
				contactsByTemplate,
				contactGroupsByTemplate,
				contactsWithNoTemplate,
				contactGroupsWithNoTemplate,
				emailAddressesWithNoTemplate
			);
		} else {
			this.buildSingleNotificationForEndEvent(
				contactsWithNoTemplate,
				contactGroupsWithNoTemplate,
				emailAddressesWithNoTemplate
			);
		}
    }

	private buildSingleNotificationForEndEvent(
		contactsWithNoTemplate: number[],
		contactGroupsWithNoTemplate: string[],
		emailAddressesWithNoTemplate: string[]
	): void {
		// Gather all Contacts and Contact Groups for this template
		const userIdsForRule = NotificationMixins.methods.combineAndRemoveDuplicates(
			[],
			contactsWithNoTemplate
		);

		const contactGroupIdsForRule = NotificationMixins.methods.combineAndRemoveDuplicates(
			[],
			contactGroupsWithNoTemplate
		);

		const emailAddressesForRule = NotificationMixins.methods.combineAndRemoveDuplicates(
			[],
			emailAddressesWithNoTemplate
		);

		// Create the Notification object with the default values
		const notification = NotificationMixins.methods.createNotification(
			userIdsForRule,
			contactGroupIdsForRule,
			emailAddressesForRule,
			this.allContacts,
			this.contactGroups,
			this.allNotificationTemplates,
			null
		);

		// Establish the Key/Values for Manual and Custom Fields for this template
		NotificationMixins.methods.createManualAndCustomFieldsForTemplate(notification, null);
		NotificationMixins.methods.bindContactsFromPreviousNotification(notification, null);

		const defaultTemplateFormatIndex = this.notificationTemplateFormats.findIndex(f => f.id === this.getNotificationTemplateDefaultFormat);
		notification.notificationTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];

		// Create a Notify modal screen for this template
		this.notificationsToSend.push(notification);
		this.useSingleNotificationUI = true;
	}

	private buildMultipleNotificationsForEndEvent(
		contactsByTemplate: Map<number, number[]>,
		contactGroupsByTemplate: Map<number, string[]>,
		contactsWithNoTemplate: number[],
		contactGroupsWithNoTemplate: string[],
		emailAddressesWithNoTemplate: string[]
	): void {
		this.notifyNotificationRules.rules.forEach((rule: NotificationRule) => {
			// If no template assigned, we do not want to create a Notify screen for it
            if (!rule.notificationTemplateId) {
				return;
			}

			if (!this.predefinedNotificationTemplates.some((t: NotificationTemplate) => 
				t.notificationTemplateId === rule.notificationTemplateId)) {
				return;
			}

			// Avoid creating multiple Notify screens for a template which belongs to multiple rules
            if (this.notificationsToSend.some((n: Notification) => 
				n.notificationTemplate?.notificationTemplateId === rule.notificationTemplateId)) {
				return;
			}

			// Gather all Contacts and Contact Groups for this template
            const userIdsForRule = NotificationMixins.methods.combineAndRemoveDuplicates(
                contactsByTemplate.get(rule.notificationTemplateId) || [],
                contactsWithNoTemplate
			);

            const contactGroupIdsForRule = NotificationMixins.methods.combineAndRemoveDuplicates(
                contactGroupsByTemplate.get(rule.notificationTemplateId) || [],
                contactGroupsWithNoTemplate
			);

			const emailAddressesForRule = NotificationMixins.methods.combineAndRemoveDuplicates(
				rule.emailAddresses || [],
				emailAddressesWithNoTemplate
			);

			// Create the Notification object with the default values
            const notification = NotificationMixins.methods.createNotification(
                userIdsForRule,
                contactGroupIdsForRule,
				emailAddressesForRule,
                this.allContacts,
                this.contactGroups,
                this.allNotificationTemplates,
                rule.notificationTemplateId,
				this.isSitRepEnabledByDefault
            );

			const sentTemplateData = this.recentEventSentTemplate(rule.notificationTemplateId);

			// Establish the Key/Values for Manual and Custom Fields for this template
            NotificationMixins.methods.createManualAndCustomFieldsForTemplate(notification, sentTemplateData);
			NotificationMixins.methods.bindContactsFromPreviousNotification(notification, sentTemplateData);
			NotificationMixins.methods.bindPreviouslySentNotificationFields(notification, sentTemplateData);

			const defaultTemplateFormatIndex = this.notificationTemplateFormats.findIndex(f => f.id === this.getNotificationTemplateDefaultFormat);
			notification.notificationTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];

			// Create a Notify modal screen for this template
            this.notificationsToSend.push(notification);
        });
	}

	private get notificationRuleContactGroups(): ContactGroup[] {
		const contactGroups: ContactGroup[] = [];

		if (this.notifyOpenedManually) {
			return [];
		}

		if (this.canUseNotificationRules) {
			this.notifyNotificationRules?.rules.forEach(rule => {
				if (rule.notificationTemplateId && rule.notificationTemplateId !== this.currentNotificationToSend?.notificationTemplate?.notificationTemplateId) {
					return;
				}

				rule.contactGroupIds.forEach(contactGroupId => {
					const contactGroup = this.contactGroups.firstOrDefault((c: ContactGroup) => c.contactGroupId == contactGroupId);
					if (contactGroup && !contactGroups.some(cg => cg.contactGroupId === contactGroup.contactGroupId)) {
						contactGroups.push(contactGroup);
					}
				});
			});
		}

		return contactGroups;
	}

	private get notificationRuleContacts(): ContactOption[] {
		const contacts: ContactOption[] = [];

		if (this.notifyOpenedManually) {
			return [];
		}

		if (this.canUseNotificationRules) {
			this.notifyNotificationRules?.rules.forEach(rule => {
				if (rule.notificationTemplateId && rule.notificationTemplateId !== this.currentNotificationToSend?.notificationTemplate?.notificationTemplateId) {
					return;
				}

				rule.userIds.forEach(userId => {
					const contact = this.allContacts.firstOrDefault((c: ContactOption) => c.userID == userId);
					if (contact && !contacts.some(c => c.userID === contact.userID)) {
						contacts.push(contact);
					}
				});

				contacts.push(...rule.emailAddresses.map(email => ({ userID: null, email: email, fullName: null })));
			});
		}

		return contacts;
	}

	private get highlightedContacts(): ContactOption[] {
		const notificationRuleContact = this.notificationRuleContacts;
		if (notificationRuleContact.length === 0) {
			return [];
		}

		if (!this.currentNotificationToSend || !this.currentNotificationToSend.notificationTemplate) {
			return [];
		}

		const prevSent = this.recentEventSentTemplate(this.currentNotificationToSend.notificationTemplate.notificationTemplateId);

		if (!prevSent) {
			return [];
		}
		
		const notificationData = JSON.parse(prevSent.notificationData) as Notification;
		if (!notificationData || !notificationData.contacts) {
			return [];
		}

		// Find contacts that are in the notificationRuleContact but not in the current notification contacts
		return filter(notificationRuleContact, (contact) => {
			return !notificationData.contacts.some(c => 
				(c.fullName == null && c.email === contact.email)
				|| (c.email == null && c.fullName === contact.fullName)
			);
		});
	}

	@Emit("notify-skipped")
	private skipSingleRule(): NotifySkipped {
		return {
			groupId: this.groupId,
			eventOutcomeId: this.eventOutcomeId,
			eventNote: this.eventNote
		};
	}

	private buildHighlightedContactGroups(): ContactGroup[] {
		const notificationRuleContact = this.notificationRuleContactGroups;
		if (notificationRuleContact.length === 0) {
			return [];
		}

		if (!this.currentNotificationToSend || !this.currentNotificationToSend.notificationTemplate) {
			return [];
		}

		const prevSent = this.recentEventSentTemplate(this.currentNotificationToSend.notificationTemplate.notificationTemplateId);

		if (!prevSent) {
			return [];
		}
		
		const notificationData = JSON.parse(prevSent.notificationData) as Notification;
		if (!notificationData || !notificationData.contactGroups) {
			return [];
		}

		// Find contacts that are in the notificationRuleContact but not in the current notification contacts
		return filter(notificationRuleContact, (contact) => {
			return !notificationData.contactGroups.some(c => 
				(c.contactGroupId === contact.contactGroupId)
			);
		});
	}

	private get isNotificationTemplatesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates"]);
	}

	private get isContactGroupsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationGroups"]);
	}

	private get isNotificationTemplatesCustomFieldsEnabled(): boolean {
		return this.getFeature(["CustomFields", "NotificationTemplates"]);
	}

	private get isNotificationTemplatesEmbeddedHtmlEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "EmbeddedHtml"]);
	}

	private get isPersistEventOutcomeEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "PersistEventOutcome"]);
	}

	private get isNotificationRulesEnabled(): boolean {
		return !!this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationRules"]);
	}

	private get isNotificationModalLayoutEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "RearrangedLayout"]);
	}

	private get isNotificationNoteEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationNote"]);
	}

	private get isPredefineTemplateEnabled(): boolean {
        return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationRules", "PredefineTemplate"]);
	}

	private get hasNotificationTemplatePermissions(): boolean {
		return this.permissions.canViewNotificationTemplates || this.permissions.canEditNotificationTemplates;
	}

	private get hasCanViewCustomFieldsPermission(): boolean {
		return this.permissions.canViewCustomFields;
	}

	private get eventNote(): string {
		return this.notifyNotificationRules?.eventNote ?? "";
	}

	private get eventOutcomePath(): EventOutcome[] {
		return this.notifyNotificationRules?.eventOutcomePath ?? [];
	}

	private get eventOutcomeId(): number {
		return this.notifyNotificationRules?.eventOutcomePath[this.notifyNotificationRules.eventOutcomePath.length - 1]?.eventOutcomeID;
	}

	private get hasNotificationRulePermissions(): boolean {
		return this.permissions.canViewNotificationRules || this.permissions.canEditNotificationRules;
	}

	private get canUseNotificationRules(): boolean {
		return this.isNotificationRulesEnabled && this.hasNotificationRulePermissions
	}

	private get notificationTemplateIdsForMatchingRules(): number[] {
		if (!this.canUseNotificationRules || !this.notifyNotificationRules?.rules) {
			return [];
		}
		var notificationTemplateIds = this.notifyNotificationRules.rules.map((rule) => {
			return rule.notificationTemplateId;
		});

		return notificationTemplateIds;
	}

	private get canUseNotificationTemplates(): boolean {
		return this.isNotificationTemplatesEnabled && this.hasNotificationTemplatePermissions;
	}

	@Watch("groupId")
	private async loadNotificationTemplates(): Promise<void> {
		if (!this.canUseNotificationTemplates) {
			return;
		}

		try {
			// Fetch all notification options
			const allNotificationTemplates = await notificationTemplatesApi.getAllNotificationTemplates(NotificationTemplateTypeId.Event, this.groupId);
			this.allNotificationTemplates = allNotificationTemplates;

			// If predefined templates enabled, filter to only those templates which correspond to the notification rules
			if (this.isPredefineTemplateEnabled) {
				// Gather templates that correspond to the provided rules
				let allNotificationTemplatesForMatchingRules: NotificationTemplate[] = [];
				allNotificationTemplatesForMatchingRules = allNotificationTemplates.filter(template => 
					this.notificationTemplateIdsForMatchingRules.includes(template.notificationTemplateId));			
				this.predefinedNotificationTemplates = allNotificationTemplatesForMatchingRules; 
			}
		} catch (ex) {
			console.error("Unexpected error fetching notification templates: " + ex);
		}
	}

	private updateModalLayout(): void {
		if (this.isNotificationModalLayoutEnabled) {
			this.layout = this.notificationModalLayout;

			const arrays = [this.layout.left, this.layout.center, this.layout.right];
			const nonEmptyCount = arrays.filter(arr => Array.isArray(arr) && arr.length > 0).length;
			this.emitModalNumberOfColumnsChanged(nonEmptyCount);

			if (nonEmptyCount !== 1) {
				return;
			}

			if (this.layout.left.length > 0) {
				this.leftColSpan = FULL_SIZE_COLUMN;
			} else if (this.layout.center.length > 0) {
				this.centerColSpan = FULL_SIZE_COLUMN;
			} else if (this.layout.right.length > 0) {
				this.rightColSpan = FULL_SIZE_COLUMN;
			}
		}
	}

	@Emit("modal-number-of-columns-changed")
	private emitModalNumberOfColumnsChanged(columnCount: number): number {
		return columnCount;
	}

	private get userIdsForNotificationRules(): number[] {
		var userIdsFromRules: number[] = [];

		if (this.canUseNotificationRules) {
			this.notifyNotificationRules?.rules.forEach(rule => {
				userIdsFromRules.push(...rule.userIds);
			})
		}

		return Array.from(new Set(userIdsFromRules))
	}

	private async getAllContacts(): Promise<void> {
		try {
			this.allContacts = await api.getContactsPaginated({
				params: {
					page: 1,
					pageSize: 0,
					searchTerm: "",
					requiredUserIds: this.userIdsForNotificationRules
				},
				cancelToken: null
			}, this.groupId);
		} catch (error) {
			console.error("Error when getting all contacts", error)
		}
	}

	private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
			await this.retrieveContactGroups(searchTerm);
		},
		getDebouncePeriod(),
		{ leading: false, trailing: true });

	@Watch("groupId")
	private async retrieveContactGroups(searchTerm: string = ""): Promise<void> {
		let params: PaginatedSearchQueryParams = {
			page: 1,
			pageSize: 50,
			sortBy: "title",
			searchTerm: searchTerm
		}

		this.contactGroupSearchToken = axios.CancelToken.source();

		let request: PaginatedSearchRequest = {
			params: params,
			cancelToken: this.contactGroupSearchToken.token
		}

		try {
			this.contactGroups = (await api.getContactGroups(request)).data;
		}
		catch (err) {
			console.error(err);
			this.$notify({
				type: "error",
				title: "Retrieving Contact Groups",
				text: err.message
			});
			return;
		}
	}

	@Watch("groupId")
	private async loadContacts(): Promise<void> {
		this.emailLoading(false);
		await this.getAllContacts();
		this.emailLoading(true);
	}

	private get notifyRules(): NotificationRule[] {
		return this.notifyNotificationRules?.rules ?? null;
	}

	private async onSend(): Promise<void> {
		await this.handleSend(this)
	}

	private handleSend = debounce(async (component: InstanceType<typeof NotificationModalContent>): Promise<void> => {
		const groupId = component.groupId;
		const eventId = component.eventId;
		const eventOutcome = component.eventOutcome;
		const payloadsWithData = this.getEmailPayload();


		if (eventOutcome.eventOutcomeId != null || (eventOutcome.eventOutcomeNote != null && eventOutcome.eventOutcomeNote.length > 0)) {
			try {
				let payload: UpdateEventOutcomeRequest = {
					eventId: this.eventId,
					eventOutcomeId: eventOutcome.eventOutcomeId,
					note: eventOutcome.eventOutcomeNote
				};
				await api.updateEventOutcomeByNotification(payload);
			}
			catch(err) {
				console.error(err);
				this.$notify({
					type: "error",
					title: "Updating Event Outcome",
					text: err.message
				});

				// Break out of function so notification doesn't get sent
				return;
			}
		}

		this.notifySent({
			groupId: groupId,
			eventOutcomeId: eventOutcome.eventOutcomeId,
			eventNote: eventOutcome.eventOutcomeNote
		});

		if (!payloadsWithData || payloadsWithData?.length === 0) {
			return;
		}

		await api.sendEventNotifications(eventId, payloadsWithData);
	}, getDebouncePeriod());

	private getEmailPayload(): EmailNotificationRequestWithData[] {
		// If ad hoc Notify modal, only send current notification
		if (this.notifyOpenedManually || !this.isPredefineTemplateEnabled || this.useSingleNotificationUI) {
			const payload = NotificationMixins.methods.notificationToEmailRequest(this.currentNotificationToSend);
			return [{
				notificationRequest: payload,
				notificationData: JSON.stringify(payload)
			}];
		} else {
			const payloadsWithData: EmailNotificationRequestWithData[] = [];
			this.notificationsToSend.forEach(notification => {
				if (notification.skipped || notification.isPreviouslySent) {
					return;
				}

				const payload = NotificationMixins.methods.notificationToEmailRequest(notification);
				payloadsWithData.push({
					notificationRequest: payload,
					notificationData: JSON.stringify(payload)
				});
			});
			return payloadsWithData;
		}
	}

	private async onPreviewTemplate(): Promise<void> {

		let override = {};
		if (this.eventOutcome?.eventOutcomeNote != null && this.eventOutcome?.eventOutcomeNote.length > 0) {
			override["eventOutcomeNote"] = this.eventOutcome.eventOutcomeNote;
		}

		// Hard coded to PDF as XSS middleware doesn't like HTML file responses
		// and this will need to be investigated
		await notificationTemplatesApi.previewNotificationTemplate(this.eventId, {
			notificationTemplate: this.selectedNotificationTemplate,
			notificationDocumentTypeId: NotificationDocumentTypeId.Pdf,
			eventOutcomeOverride: this.eventOutcome?.eventOutcomeId,
			placeholderOverrides: override,
			note: this.note,
			manualInputFields: this.currentNotificationToSend.notificationManualFields != null ? this.currentNotificationToSend.notificationManualFields.reduce((map, obj) => {
				map[obj.field.id] = obj.value;
				return map;
			}, {}) : [],
			customFieldInputs: this.currentNotificationToSend.notificationCustomFields != null ? this.currentNotificationToSend.notificationCustomFields.reduce((map, obj) => {
				map[obj.field.id] = obj.value;
				return map;
			}, {}) : []
		});
	}

	@Watch("selectedNotificationTemplate")
	private updateAvailableManualFields(newVal: NotificationTemplate, oldVal: NotificationTemplate): void {
		if (this.selectedNotificationTemplate == null) {
			this.selectedTemplateFormat = null;
			return;
		}

		if (newVal === oldVal) {
			return;
		}

		this.isComponentReady = false;
		const sentTemplateData = this.recentEventSentTemplate(this.selectedNotificationTemplate?.notificationTemplateId);

		if (this.isContactGroupsEnabled && !!this.selectedNotificationTemplate.contactGroups) {
			this.selectedNotificationTemplate.contactGroups.forEach(contactGroup => {
				if (!this.selectedContactGroups.some(id => id.contactGroupId === contactGroup.contactGroupId)) {
					this.selectedContactGroups.push(contactGroup);
				}
			});

			if (!this.notifyOpenedManually) {
				this.highlightedContactGroups = this.buildHighlightedContactGroups();
			}
		}
		// Load the manual/custom fields in ad hoc Notify modal
		if (this.notifyOpenedManually || (!this.notifyOpenedManually && (!this.isPredefineTemplateEnabled || this.useSingleNotificationUI))) {
			if (this.selectedTemplateFormat === null) {
				this.setDefaultTemplateFormat();
			}

			this.currentNotificationToSend.notificationTemplate = this.selectedNotificationTemplate;
			NotificationMixins.methods.createManualAndCustomFieldsForTemplate(this.currentNotificationToSend, sentTemplateData ?? null);
			NotificationMixins.methods.bindContactsFromPreviousNotification(this.currentNotificationToSend, sentTemplateData ?? null);
			NotificationMixins.methods.bindPreviouslySentNotificationFields(this.currentNotificationToSend, sentTemplateData ?? null);
		}
		this.isComponentReady = true;
	}

	private async retrieveSentTemplates(): Promise<void> {
		if (!this.canUseNotificationTemplates) {
			return;
		}

		this.sentTemplates = await eventsApi.getSentEventNotifications(this.eventId);
	}

	private recentEventSentTemplate(templateId: number): EventNotification | null {
		if (!this.sentTemplates || this.sentTemplates.length === 0) {
			return null;
		}

		const sortedSentTemplates = this.sentTemplates
			.sort((a: EventNotification, b: EventNotification) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
			.find(st => st.notificationTemplateId === templateId);

		return sortedSentTemplates ?? null;
	}

	@Watch("currentNotificationToSend.notificationTemplateFormat")
	public onNotificationTemplateFormatChanged(newFormat: NotificationTemplateFormatOption): void {
		this.selectedTemplateFormat = newFormat;
	}

	private get errorsExistInTemplates(): boolean {
		// Return false if all templates are skipped
		if (this.notificationsToSend.every(n => n.skipped)) {
			return false;
		}

		const allErrors = Array.from(this.notificationValidationErrorsByTemplate.values());
		return allErrors.some(errors => errors.length > 0);
	}

	private async eventOutcomeDetailsChanged(newValue: EventOutcomeAndNote): Promise<void> {
		if (this.canUseNotificationRules && !this.notifyOpenedManually) {
			this.emitNotifyRuleReload({
				eventId: this.eventId,
				eventOutcomePath: newValue.eventOutcomePath,
				eventOutcomeNote: newValue.eventOutcomeNote
			})
		}
		this.eventOutcome = newValue;

		if (this.isPersistEventOutcomeEnabled && this.eventOutcome?.eventOutcomeId != null) {
			this.emitEventOutcomeChange(this.eventOutcome);
		}
	}

	private get sentTemplateMessage() : string {
		const sentTemplate = this.sentTemplates.find(st => st.notificationTemplateId === this.selectedNotificationTemplate?.notificationTemplateId);
		if (!sentTemplate) {
			return "";
		}

		return `Template was sent at ${new Date(sentTemplate.sentAt).toLocaleString()} by ${sentTemplate.userName}. All sent information is detailed in the fields below `;
	}

	@Emit("event-outcome-changed")
	private emitEventOutcomeChange(newValue: EventOutcomeAndNote): EventOutcomeAndNote {
		return newValue;
	}

	private setDefaultTemplateFormat(): void {
		if (this.isNotificationTemplatesEnabled) {
			const defaultTemplateFormatIndex = this.notificationTemplateFormats.findIndex(f => f.id === this.getNotificationTemplateDefaultFormat);
			this.selectedTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];
			this.currentNotificationToSend.notificationTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];
		}
	}

	private onSearch(searchTerm: string): void{
		this.handleSearchInput(searchTerm);
	}

	private getCustomFieldSetting(customField: NotificationTemplateManualField): CustomFieldDto {
		let customFieldSetting = this.orderedCustomFieldList.find(cf => cf.id == customField.id);
		return customFieldSetting;
	}

	private getCustomFieldValue(customField: NotificationTemplateManualField): CustomFieldValues {
		let value: string = "";
		const existing: ManualFieldInput = this.currentNotificationToSend.notificationCustomFields.firstOrDefault(cf => cf.field?.id == customField?.id);

		if (existing) {
			value = existing.value;
		}

		const customFieldValue = {
			id: customField.id,
			value: value
		}

		return customFieldValue;
	}

	private get currentNotificationToSend(): NotificationState {
		return this.notificationsToSend[this.footerStep - 1];
	}

	private get isSkipped(): boolean {
		return this.currentNotificationToSend?.skipped;
	}

	private onCustomFieldValueChanged(updatedValue: CustomFieldValues): void {
		const customFieldBeingChanged = this.currentNotificationToSend.notificationCustomFields.find(cf => cf.field.id === updatedValue.id);
		if (customFieldBeingChanged) {
			customFieldBeingChanged.value = updatedValue.value;
		}
	}

	private get canDisplayCustomFields(): boolean {
		return this.hasCustomFieldPermissions
		&& this.selectedNotificationTemplate.customFields
		&& this.selectedNotificationTemplate.customFields.length > 0;
	}

	private get hasCustomFieldPermissions(): boolean {
		return this.selectedNotificationTemplate != null
		&& this.isNotificationTemplatesCustomFieldsEnabled
		&& this.hasCanViewCustomFieldsPermission;
	}

	private get notificationTemplateDropdownDisabled(): boolean {
		return this.currentNotificationToSend && !this.notifyOpenedManually && this.isPredefineTemplateEnabled && !this.notificationRulesHasNoTemplate;
	}

	private sortCustomFieldsByOrder(customFields: NotificationTemplateManualField[]): NotificationTemplateManualField[] {
		var orderedCustomFields: NotificationTemplateManualField[] = [];

		this.orderedCustomFieldList.forEach(ocf => {
			var matchedCustomField = customFields.find(cf => cf.id == ocf.id);

			if (matchedCustomField) {
				orderedCustomFields.push(matchedCustomField);
			}
		});

		return orderedCustomFields;
	}


	private get getNotificationTemplateFormats(): NotificationTemplateFormatOption[] {
		if(!this.isNotificationTemplatesEmbeddedHtmlEnabled ) {
			// Filter out the HTML format if the feature is not enabled
			return this.notificationTemplateFormats.filter((format: NotificationTemplateFormatOption) => {
				return format.id !== NotificationDocumentTypeId.Html;
			});
		}
		else {
			return this.notificationTemplateFormats;
		}
	}

	private skipNotify(): void {
		if(!this.notifyOpenedManually && (this.useSingleNotificationUI || !this.isPredefineTemplateEnabled)) {
			this.skipSingleRule();
			return;
		}

		this.setSkipForCurrentTemplate(true);
		this.onNextFooterStep();
	}

	private unskipNotify(): void {
		this.setSkipForCurrentTemplate(false);
	}

	private editNotify(): void {
		this.currentNotificationPreviouslySent = false;
		this.notificationsToSend[this.footerStep - 1].isPreviouslySent = false;
	}

	private setSkipForCurrentTemplate(value: boolean): void {
		this.$set(this.notificationsToSend, this.footerStep - 1, {
			...this.notificationsToSend[this.footerStep - 1],
			skipped: value
		});
	}

	@Emit("close-modal")
	private sendingCancel(): boolean {
		return true;
	}

	@Emit("email-loading")
	private emailLoading(emailsLoaded: boolean): boolean {
		return emailsLoaded;
	}

	@Emit("focus-changed")
	private onInputFocusChanged(focus: boolean): boolean {
		if (this.isPoppedOut) {
			return focus;
		}
	}

	@Emit("notify-rule-reload")
	private emitNotifyRuleReload(notifyRuleReload): NotifyRuleReload {
		return notifyRuleReload;
	}

	@Emit("notify-sent")
	private notifySent(notifySent: NotifySent): NotifySent {
		return notifySent;
	}

	@Watch("selectedNotificationTemplate")
	@Emit("template-changed")
	private onTemplateChanged(newTemplate: NotificationTemplate): NotificationTemplate {
		return newTemplate;
	}

	private async loadDefaultTemplate(): Promise<void> {
		this.defaultNotificationTemplateId = await organizationApi.GetDefaultNotificationTemplateId(this.groupId);

		if (this.defaultNotificationTemplateId == null && (
				this.allNotificationTemplates == null
				|| this.allNotificationTemplates.length === 1)
		) {
			// If no default returned and there is only one option in the list, use the first template in the list
			this.selectedNotificationTemplate = this.allNotificationTemplates[0] ?? null; 
		}

		const selectedTemplate = this.allNotificationTemplates.firstOrDefault((template: NotificationTemplate) => {
			return template.notificationTemplateId === this.defaultNotificationTemplateId;
		});

		if (selectedTemplate) {
			this.currentNotificationToSend.notificationTemplate = selectedTemplate;
		}
	}

	private get notificationRuleTooltip(): string {
		if (!this.notifyRules) {
			return "";
		}

		return `Contacts have been assigned using the following Notification Rules: ${this.notifyRules.map(rule => rule.title).join(", ")}`;
	}

	private get showOverlay(): boolean {
		return this.currentNotificationSkipped || this.currentNotificationPreviouslySent;
	}

	private get isPreviouslySent(): boolean {
		return this.currentNotificationToSend?.isPreviouslySent;
	}

	// Use NotificationMixins for validation
	private updateNotificationValidationErrors(): void {
		if (this.notificationRulesHasNoTemplate) {
			this.manualValidation();
			return;
		}

		NotificationMixins.methods.updateNotificationValidationErrors(
			this.notificationsToSend,
			this.eventOutcome,
			this.hasCustomFieldPermissions,
			this.notifyNotificationRules,
			this.notificationValidationErrorsByTemplate
		);
		// Force reactivity if needed
		this.notificationValidationErrorsByTemplate = new Map(this.notificationValidationErrorsByTemplate);
	}

	private validateNotification(notification: Notification): void {
		NotificationMixins.methods.validateNotification(
			notification,
			this.eventOutcome,
			this.hasCustomFieldPermissions,
			this.notifyNotificationRules,
			this.orderedCustomFieldList,
			this.notificationValidationErrorsByTemplate,
		);
		this.notificationValidationErrorsByTemplate = new Map(this.notificationValidationErrorsByTemplate);
	}

	private get isOnFinalTemplateStep(): boolean {
		return this.footerStep === this.footerStepTotal;
	}

	private get maxFooterStep(): number {
		return this.notificationsToSend.length;
	}

	private get isOnLandingPageFooterStep(): boolean {
		return this.footerStep === this.landingPageFooterStep;
	}

	private isOptionInRuleAndNotSelected(option: ContactGroup): boolean {
		if (!this.canUseNotificationRules) {
			return false;
		}

		const optionInRule = this.notificationRuleContactGroups.some(cg => cg.contactGroupId === option.contactGroupId);
		const optionSelected = this.currentNotificationToSend.contactGroups.some(cg => cg.contactGroupId === option.contactGroupId);

		return optionInRule && !optionSelected; 
	}

	@Watch("currentNotificationToSend", { deep: true })
	private onManualNotificationChangeValidate(): void { 
		this.currentNotificationPreviouslySent = this.currentNotificationToSend.isPreviouslySent;
		this.currentNotificationSkipped = this.currentNotificationToSend.skipped;
		this.manualValidation();
	}

	@Watch("eventOutcome", { deep: true })
	private onEventOutcomeChangeManualValidate(): void { 
		this.manualValidation();
	}

	private manualValidation(): void {
		if (!this.notifyOpenedManually && this.isPredefineTemplateEnabled && !this.notificationRulesHasNoTemplate) {
			return;
		}
		this.validateNotification(this.currentNotificationToSend);

		if (!this.currentNotificationToSend) {
			this.manualNotificationIsValid = false;
			return;
		}
		const errorMapKey = this.currentNotificationToSend.notificationTemplate?.notificationTemplateId ?? NoNotificationTemplateErrorIdentifier;
		if (this.notificationValidationErrorsByTemplate.get(errorMapKey).length === 0 
			|| (errorMapKey === NoNotificationTemplateErrorIdentifier && !this.notifyOpenedManually)) {
			this.manualNotificationIsValid = true;
		} else {
			this.manualNotificationIsValid = false;
		}
	}

	private get sendButtonEnabled(): boolean {
		if (this.notifyOpenedManually || !this.isPredefineTemplateEnabled || this.useSingleNotificationUI) {
			return this.manualNotificationIsValid;
		} else {
			return !this.errorsExistInTemplates && this.isOnLandingPageFooterStep;
		}
	}

	private get noteValid(): boolean {
		if (!this.isNotificationNoteEnabled) {
			return false;
		}

		return this.currentNotificationToSend.note.length > NotificationNoteMaxLength;
	}
	private get noteInvalidFeedback(): string {
		if (this.isNotificationNoteEnabled && this.currentNotificationToSend.note.length > NotificationNoteMaxLength) {
			return `Note must have no more than ${NotificationNoteMaxLength} letters.`;
		}

		return "";
	}

	private get addressesValid(): boolean {
		if (this.currentNotificationToSend.contacts.length === 0 && this.recipientInputsTouched) {
			if (this.isContactGroupsEnabled) {
				const hasContactGroups = this.currentNotificationToSend.contactGroups && this.currentNotificationToSend.contactGroups.length > 0;
				if (!hasContactGroups) {
					return false;
				}
			} else {
				// Has no Contact Groups Feature, and no Contacts
				return false;
			}
		}

		return true;
	}

	private get notificationRulesHasNoTemplate(): boolean {
		if (!this.notifyNotificationRules || !this.notifyNotificationRules.rules) {
			return false;
		}

		if (this.notifyOpenedManually) {
			return false;
		}

		return this.notifyNotificationRules.rules.every(rule => !rule.notificationTemplateId);
	}

	private get addressesInvalidFeedback(): string {
		if (this.currentNotificationToSend.contacts.length === 0) {
			let feedbackString = "Please specify at least one recipient";

			if (this.isContactGroupsEnabled) {
				feedbackString = feedbackString.concat(" (or contact group)");
			}

			return feedbackString;
		}

		return "";
	}

	private get contactGroupsValid(): boolean {
		// Check if there are any contact groups or contacts
		const hasGroups = this.currentNotificationToSend.contactGroups && this.currentNotificationToSend.contactGroups.length > 0;
		const hasContacts = this.currentNotificationToSend.contacts && this.currentNotificationToSend.contacts.length > 0;
		return !this.recipientInputsTouched || (hasGroups || hasContacts);
	}

	private get contactGroupsInvalidFeedback(): string {
		const hasGroups = this.currentNotificationToSend.contactGroups && this.currentNotificationToSend.contactGroups.length > 0;
		const hasContacts = this.currentNotificationToSend.contacts && this.currentNotificationToSend.contacts.length > 0;
		if (!(hasGroups || hasContacts)) {
			return "Please specify at least one contact group (or contact)";
		}
		return "";
	}

	private get noteValidFeedback() {
		const charactersUsed = this.currentNotificationToSend.note !== null ? this.currentNotificationToSend.note.length : 0;
		return `Characters used: ${charactersUsed}/${NotificationNoteMaxLength}`;
	}

	private get previouslySendDetails(): SentOverlayDetails {
		const sentTemplate = this.sentTemplates.find(st => st.notificationTemplateId === this.selectedNotificationTemplate?.notificationTemplateId);
		const parsedData = sentTemplate?.notificationData ? JSON.parse(sentTemplate.notificationData) as Notification : null;

		return {
			sentBy: sentTemplate?.userName,
			sentAt: sentTemplate ? new Date(sentTemplate.sentAt).toLocaleString(): null,
			notificationTitle: this.currentNotificationToSend.notificationTemplate?.title,
			contactsNames: parsedData.contacts.map(c => c.fullName || c.email).join(", "),
			contactGroupsTitles: parsedData.contactGroups.map(cg => cg.title).join(", "),
			hasAdditionalContacts: this.currentNotificationToSend.hasContactsDifferentToRule,

		}
	}

	private get allTemplatesSkipped(): boolean {
		return this.notificationsToSend.every(n => n.skipped);
	}

	private onActionPlanResult(value: boolean): void {
		this.showActionPlanAlert = value;
	}

	private isOptionHighlighted(option: ContactGroup): boolean {
		if (!this.highlightedContactGroups || this.highlightedContactGroups.length === 0) {
			return false;
		}

		return this.highlightedContactGroups.some(hc => hc.contactGroupId === option.contactGroupId);
	}

	private setRecipientsTouched(): void { 
		if (!this.recipientInputsTouched) {
			this.recipientInputsTouched = true;
		}
	}
}
